/**
 * T2 Business Dashboard — the headline row: five tiles across, divided by hairlines.
 *
 * The layout is the supplied design reference (Supply Chain Dashboard v5) rather than the card
 * grid the rest of the portal uses: one edge-to-edge band, rules instead of borders, a micro
 * uppercase label over a large figure over a 3px track. Colours come from the `--t2d-*` tokens in
 * `index.css`, which carry the reference's own palette in both themes.
 *
 * **Every figure comes from the server's `kpis` view; nothing here computes a measure.** SDP is
 * `SUM(on time) / SUM(delivered)` and each share is a share of the whole filtered result, so
 * re-deriving either from another panel's rows would give a plausible wrong answer (§4.3,
 * [DATA-1]). The shapes and helpers live in `contract.ts`.
 *
 * The tiles deliberately do not move with the order state filter, which is why `report.yaml` scopes
 * that filter away from the `kpis` view: the row reports ordered, received and open side by side.
 * Received and open split on delivery presence, so they add back to ordered exactly, and SDP is
 * always measured over the whole filtered population — an open line has no delivery, so letting the
 * filter drop rows would empty the SDP denominator and blank the KPI.
 *
 * A tile's colour is fixed by the reference, not decided here: open is gold and past due is red
 * because that is what the design says those tiles are, the same in every period. Only the SDP tile
 * varies, and only when the target is met — the target is 100%, so a "below target" treatment would
 * fire on every real period and say nothing. There is no amber middle band; the reference derived
 * one from `target - 8` / `target - 20` and no owner set those widths (PORT-2).
 */

import { Skeleton } from '@/components/ui/skeleton';
import { integer } from '@/components/report/groupedColumns';
import { cn } from '@/lib/utils';

import {
  SDP_TARGET_PCT,
  measureFigure,
  measureLabel,
  meetsTarget,
  pct,
  widthPct,
  type KpiRow,
} from './contract';

/** A count as the page shows it. An absent count is an em dash, never a zero. */
function count(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? integer.format(value) : '—';
}

/**
 * The measure named mid-sentence — "63.9% of volume ordered". Only the first letter changes, so
 * "Value (HKD)" keeps its currency code.
 */
function measureWord(measure: string): string {
  const label = measureLabel(measure);
  return label.charAt(0).toLowerCase() + label.slice(1);
}

interface Tile {
  label: string;
  figure: string;
  unit?: string;
  /** The bar's fill, already clamped by `widthPct`. */
  fill: string;
  /** Tailwind class for the figure. Reference colour, `-ink` so it is legible as type on white. */
  ink: string;
  /** Tailwind class for the bar. The reference value, which is what a fill wants. */
  bar: string;
  note: React.ReactNode;
}

function tiles(row: KpiRow | undefined, measure: string): Tile[] {
  const sdpMet = meetsTarget(row?.sdp_pct, row?.sdp_on_time_qty, row?.sdp_delivered_qty) === true;

  return [
    {
      label: 'Total ordered',
      figure: measureFigure(row?.ordered_measure),
      unit: measure === 'value' ? 'HKD' : 'pcs',
      // The whole book is the bar every other share is read against, so it fills its own track.
      fill: row === undefined ? '0%' : '100%',
      ink: 'text-t2d-ink',
      bar: 'bg-t2d-ink/70',
      note: (
        <>
          {count(row?.po_count)} orders · {count(row?.supplier_count)} suppliers
        </>
      ),
    },
    {
      label: 'SDP',
      figure: pct(row?.sdp_pct),
      fill: widthPct(row?.sdp_pct),
      ink: sdpMet ? 'text-t2d-good-ink' : 'text-t2d-ink',
      bar: sdpMet ? 'bg-t2d-good' : 'bg-t2d-ink/70',
      note: (
        <>
          {measureFigure(row?.sdp_on_time_qty)} of {measureFigure(row?.sdp_delivered_qty)} on time ·
          target {pct(SDP_TARGET_PCT, 0)}
        </>
      ),
    },
    {
      label: 'Received',
      figure: measureFigure(row?.received_measure),
      unit: measure === 'value' ? 'HKD' : 'pcs',
      fill: widthPct(row?.received_share),
      ink: 'text-t2d-ink',
      bar: 'bg-t2d-ink/70',
      note: (
        <>
          {pct(row?.received_share)} of {measureWord(measure)} ordered
        </>
      ),
    },
    {
      label: 'Open on order',
      figure: measureFigure(row?.open_measure),
      unit: measure === 'value' ? 'HKD' : 'pcs',
      fill: widthPct(row?.open_share),
      ink: 'text-t2d-gold-ink',
      bar: 'bg-t2d-gold',
      note: (
        <>
          {pct(row?.open_share)} of ordered · {count(row?.open_po_count)} orders
        </>
      ),
    },
    {
      label: 'Open past due 30+ days',
      figure: measureFigure(row?.past_due_measure),
      unit: measure === 'value' ? 'HKD' : 'pcs',
      fill: widthPct(row?.past_due_share),
      ink: 'text-t2d-bad-ink',
      bar: 'bg-t2d-bad',
      note: <>{pct(row?.past_due_share)} of the open book</>,
    },
  ];
}

export interface KpiCardsProps {
  /** Undefined until the one row arrives; the tiles then show em dashes, never zeros. */
  row: KpiRow | undefined;
  measure: string;
  isLoading: boolean;
}

/** The reference's own micro label: 11px, wide tracking, muted. */
const LABEL = 'text-[11px] font-medium uppercase tracking-[0.15em] text-t2d-muted';

export function KpiCards({ row, measure, isLoading }: KpiCardsProps) {
  const items = tiles(row, measure);

  return (
    <div className="grid grid-cols-1 border-y border-t2d-line sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {items.map((tile) => (
        <div
          key={tile.label}
          className="flex min-w-0 flex-col gap-1.5 border-t2d-line px-6 pt-4 pb-[15px] not-first:border-t xl:border-l xl:not-first:border-t-0"
        >
          <span className={cn(LABEL, 'truncate')}>{tile.label}</span>

          {isLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <div className="flex items-baseline gap-2">
              <span
                className={cn(
                  'text-[33px] leading-none font-semibold tracking-[-0.02em] tabular-nums',
                  tile.ink,
                )}
              >
                {tile.figure}
              </span>
              {tile.unit === undefined ? null : (
                <span className="text-[13px] whitespace-nowrap text-t2d-muted">{tile.unit}</span>
              )}
            </div>
          )}

          {/* The one inline style §4.8 allows: a runtime-computed width. */}
          <div className="relative h-[3px] bg-t2d-track">
            <div className={cn('absolute inset-y-0 left-0', tile.bar)} style={{ width: tile.fill }} />
          </div>

          <span className="truncate text-xs text-t2d-muted tabular-nums">{tile.note}</span>
        </div>
      ))}
    </div>
  );
}
